import { novaSonic } from "../services/nova";
import { detectIntent, extractTemplateId } from '../utils/intentDetection';
import { dbService } from '../services/dbService';
import { authManager } from '../managers/AuthManager';

export class ConversationManager {
    constructor() {
        this.state = "idle";
        this.subscribers = [];
        this.novaSonic = novaSonic;
        this.uiMode = "idle";

        this.history = [];
        this.documentState = null;
        this.responseSubscribers = [];
        this.cursorSubscribers = [];
        this.savingSubscribers = [];
        this.isSaving = false;
        this.saveTimeout = null;
    }

    getDocumentState() {
        return this.documentState;
    }

    subscribeDocument(callback) {
        this.documentSubscribers ??= [];
        this.documentSubscribers.push(callback);
        callback(this.documentState);

        return () => {
            this.documentSubscribers = this.documentSubscribers.filter(cb => cb !== callback);
        };
    }

    notifyDocumentSubscribers() {
        if (!this.documentSubscribers) return;
        this.documentSubscribers.forEach(cb => cb(this.documentState));
        this.triggerAutoSave();
    }

    triggerAutoSave() {
        const user = authManager.getCurrentUser();
        if (!user?.email || !this.documentState) return;

        if (this.saveTimeout) clearTimeout(this.saveTimeout);
        
        this.saveTimeout = setTimeout(async () => {
            this.notifySavingSubscribers(true);
            try {
                // Ensure the document has an ID for saving
                if (!this.documentState.id) {
                    this.documentState.id = `resume_${Date.now()}`;
                }
                await dbService.saveResume(user.email, this.documentState);
                
                // Keep "Saved" visible for a moment
                setTimeout(() => this.notifySavingSubscribers(false), 2000);
            } catch (err) {
                console.error("Auto-save failed:", err);
                this.notifySavingSubscribers(false);
            }
        }, 3000); // 3 second debounce
    }

    subscribeResponse(callback) {
        this.responseSubscribers.push(callback);
        return () => {
            this.responseSubscribers = this.responseSubscribers.filter(cb => cb !== callback);
        };
    }

    subscribe(callback) {
        this.subscribers.push(callback);
        callback(this.state);
        return () => {
            this.subscribers = this.subscribers.filter(cb => cb !== callback);
        };
    }

    subscribeCursor(callback) {
        this.cursorSubscribers.push(callback);
        return () => {
            this.cursorSubscribers = this.cursorSubscribers.filter(cb => cb !== callback);
        };
    }

    subscribeSaving(callback) {
        this.savingSubscribers.push(callback);
        callback(this.isSaving);
        return () => {
            this.savingSubscribers = this.savingSubscribers.filter(cb => cb !== callback);
        };
    }

    notifySavingSubscribers(status) {
        this.isSaving = status;
        this.savingSubscribers.forEach(cb => cb(status));
    }

    notifySubscribers() {
        this.subscribers.forEach(cb => cb(this.state));
    }

    setState(newState) {
        this.state = newState;
        this.notifySubscribers();
    }

    async startSession() {
        this.novaSonic.connect();

        this.novaSonic.onState((state) => {
        });

        this.novaSonic.onResponse((responseText) => {
            this.handleNovaSonicResponse(responseText);
        });

        this.novaSonic.onAudio((transcript) => {
            this.userTranscript = transcript;
        });
    }

    async startListening() {
        if (this.state === "speaking") {
            this.interrupt();
        }
        this.setState("listening");

        const systemPrompt = this.buildSystemPrompt();
        this.novaSonic.startListening(systemPrompt);
    }

    async stopListening() {
        if (this.state !== "listening") return;
        this.setState("thinking");
        this.novaSonic.stopListening();
    }

    interrupt() {
        this.novaSonic.stopListening();
        this.setState("idle");
    }

    buildSystemPrompt() {
        let prompt = `You are a friendly, helpful voice assistant specialized in career advice and resume building. Have natural conversations with the user, but ONLY discuss topics related to jobs, career paths, and resume creation. If the user asks about unrelated topics (like food, sports, or random facts), politely redirect them back to their resume or job search. Keep responses brief and conversational — one or two sentences when possible. Do not use markdown formatting.`;

        if (this.documentState) {
            const sectionList = this.documentState.sections
                .map(s => `- Section "${s.heading}" (id: ${s.id}): ${(s.content || "").substring(0, 100)}`)
                .join("\n");

            prompt += `

The user is editing a resume document. Here is the current state:
- Name (title): "${this.documentState.title || "Your Name"}"
- Professional Title (subtitle): "${this.documentState.subtitle || "Professional Title"}"
- Email: "${this.documentState.contact?.email || ""}"
- Phone: "${this.documentState.contact?.phone || ""}"
- Location: "${this.documentState.contact?.location || ""}"
Sections:
${sectionList}

IMPORTANT: When the user asks you to change something, you MUST include edit commands in your response using this exact format:
[EDIT:field=new value]

Available fields:
- title (for the person's name)
- subtitle (for the professional/job title)
- email, phone, location (for contact info)
- section:SECTION_ID (for section content, e.g. section:summary, section:experience, section:education, section:skills)

Examples:
- User says "change my name to John Smith" → respond: "I've updated your name! [EDIT:title=John Smith]"
- User says "my title is Software Engineer" → respond: "Updated your title! [EDIT:subtitle=Software Engineer]"
- User says "update my email to john@gmail.com" → respond: "Done! [EDIT:email=john@gmail.com]"
- User says "rewrite my summary" → respond: "Here's a new summary! [EDIT:section:summary=Experienced software engineer with 5 years of expertise in full-stack development.]"

Always include the [EDIT:...] command when making changes. You can include multiple commands in one response.`;
        } else {
            prompt += `

No document is loaded yet. If the user wants to create or edit a document, let them know you can help and suggest looking at templates. Otherwise, just have a natural conversation.`;
        }

        return prompt;
    }

    async handleNovaSonicResponse(responseText) {
        try {
            if (!responseText) {
                this.setState("idle");
                return;
            }

            let displayText = responseText;

            if (this.documentState) {
                const { cleanText } = this.parseAndApplyEdits(responseText);
                displayText = cleanText;
            }

            this.history.push({ role: "assistant", content: displayText });

            this.responseSubscribers.forEach(cb => cb(displayText));

            const lower = displayText.toLowerCase();
            if (!this.documentState &&
                (lower.includes("template") || lower.includes("get started") || lower.includes("choose"))) {
                const { stateManager } = await import("./StateManager");
                stateManager.showTemplateSelection();
            }

            this.setState("speaking");
            if ('speechSynthesis' in window) {
                const utterance = new SpeechSynthesisUtterance(displayText);
                utterance.onend = () => {
                    this.setState("idle");
                };
                utterance.onerror = (error) => {
                    this.setState("idle");
                };
                window.speechSynthesis.speak(utterance);
            } else {
                setTimeout(() => this.setState("idle"), 2000);
            }

        } catch (error) {
            this.setState("idle");
        }
    }

    parseAndApplyEdits(text) {
        const editPattern = /\[EDIT:([^\]=]+)=([^\]]*)\]/g;
        let changed = false;
        let match;
        let lastEditedFieldId = null;

        let nextState = structuredClone(this.documentState);

        while ((match = editPattern.exec(text)) !== null) {
            const field = match[1].trim().toLowerCase();
            const value = match[2].trim();

            if (field === "title") lastEditedFieldId = "field-title";
            else if (field === "subtitle") lastEditedFieldId = "field-subtitle";
            else if (field === "email") lastEditedFieldId = "field-email";
            else if (field === "phone") lastEditedFieldId = "field-phone";
            else if (field === "location") lastEditedFieldId = "field-location";
            else if (field.startsWith("section:")) lastEditedFieldId = `field-${field}`;

            if (field === "title") {
                nextState.title = value;
                changed = true;
            } else if (field === "subtitle") {
                nextState.subtitle = value;
                changed = true;
            } else if (field === "email") {
                if (!nextState.contact) nextState.contact = {};
                nextState.contact.email = value;
                changed = true;
            } else if (field === "phone") {
                if (!nextState.contact) nextState.contact = {};
                nextState.contact.phone = value;
                changed = true;
            } else if (field === "location") {
                if (!nextState.contact) nextState.contact = {};
                nextState.contact.location = value;
                changed = true;
            } else if (field.startsWith("section:")) {
                const sectionId = field.split(":")[1];
                const section = nextState.sections.find(
                    s => s.id === sectionId || s.heading.toLowerCase() === sectionId
                );
                if (section) {
                    section.content = value;
                    changed = true;
                } else {
                }
            } else {
            }
        }

        if (changed) {
            this.documentState = nextState;
            this.notifyDocumentSubscribers();

            if (lastEditedFieldId) {
                setTimeout(() => {
                    const el = document.getElementById(lastEditedFieldId);
                    if (el) {
                        const rect = el.getBoundingClientRect();

                        this.cursorSubscribers.forEach(cb => cb({
                            x: rect.left + (rect.width * 0.8),
                            y: rect.top + (rect.height * 0.8),
                            visible: true,
                            text: "Applying edit..."
                        }));

                        el.classList.remove('ai-flash-highlight');
                        void el.offsetWidth;
                        el.classList.add('ai-flash-highlight');

                        setTimeout(() => {
                            this.cursorSubscribers.forEach(cb => cb({
                                x: rect.left + (rect.width * 0.8),
                                y: rect.top + (rect.height * 0.8),
                                visible: false,
                                text: ""
                            }));
                        }, 2000);
                    }
                }, 100);
            }
        }

        const cleanText = text.replace(/\s*\[EDIT:[^\]]*\]/g, "").trim();

        return { cleanText, changed };
    }

    updateFieldDirectly(fieldId, newValue) {
        if (!this.documentState) return;

        let changed = false;
        let nextState = structuredClone(this.documentState);

        if (fieldId === "title") {
            nextState.title = newValue;
            changed = true;
        } else if (fieldId === "subtitle") {
            nextState.subtitle = newValue;
            changed = true;
        } else if (fieldId === "email") {
            if (!nextState.contact) nextState.contact = {};
            nextState.contact.email = newValue;
            changed = true;
        } else if (fieldId === "phone") {
            if (!nextState.contact) nextState.contact = {};
            nextState.contact.phone = newValue;
            changed = true;
        } else if (fieldId === "location") {
            if (!nextState.contact) nextState.contact = {};
            nextState.contact.location = newValue;
            changed = true;
        } else if (fieldId === "profilePhoto") {
            nextState.profilePhotoUrl = newValue;
            changed = true;
        } else if (fieldId.startsWith("section:")) {
            const sectionId = fieldId.split(":")[1];
            const section = nextState.sections.find(
                s => s.id === sectionId || s.heading.toLowerCase() === sectionId
            );
            if (section) {
                section.content = newValue;
                changed = true;
            }
        }

        if (changed) {
            this.documentState = nextState;
            this.notifyDocumentSubscribers();
            this.responseSubscribers.forEach(cb => cb("Manual edit saved."));
        }
    }

    initializeDocument(templateState) {
        const state = templateState.initialState
            ? structuredClone(templateState.initialState)
            : structuredClone(templateState);

        const keepExistingText = !!this.documentState;

        this.documentState = {
            ...state,
            templateId: templateState.id || state.id || state.templateId,
            // Always use the NEW template's layout/style/source — never inherit these from the old document
            source: templateState.source || state.source,
            layout: templateState.layout || state.layout,
            colors: templateState.colors || state.colors,
            style: templateState.style || state.style,
            // Preserve existing text fields only if switching templates (not first load)
            title: keepExistingText && this.documentState.title ? this.documentState.title : state.title,
            subtitle: keepExistingText && this.documentState.subtitle ? this.documentState.subtitle : state.subtitle,
            contact: keepExistingText && this.documentState.contact ? structuredClone(this.documentState.contact) : state.contact,
        };

        if (keepExistingText && this.documentState.sections) {
            this.documentState.sections = this.documentState.sections.map(newSection => {
                const existingSection = this.documentState.sections.find(s => s.id === newSection.id || s.heading === newSection.heading);
                if (existingSection) {
                    newSection.content = existingSection.content;
                }
                return newSection;
            });
        }

        this.notifyDocumentSubscribers();
    }

    reset() {
        this.documentState = null;
        this.history = [];
        this.setState("idle");
        this.notifyDocumentSubscribers();
    }
}

export const conversationManager = new ConversationManager();
